import { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import GrantPicker, { Dashboard, Level, levelOf } from './GrantPicker';

// Per-user, per-dashboard access. One row per dashboard, three states:
// none / view / edit. The user's ROLE (prod-view or dev-view) decides which
// environments these apply in — this screen only decides what.
//
// Ticking is a DRAFT: nothing is written until שמור. Access changes are the kind of
// thing you want to look over as a whole before committing, and a stream of little
// writes would leave someone half-granted while you were still deciding.
//
// Admins are not listed here: admin means every dashboard, both environments,
// with edit, and there is nothing to tick.
//
// The list itself is `GrantPicker`, shared with the Create-user dialog so that
// granting on day one and changing it later offer the same choice.

interface Props {
  email: string;
  role: string;
  /** Disabled Cognito account — may only have access taken away, never given. */
  disabled?: boolean;
  onClose: () => void;
}

export default function DashboardGrants({ email, role, disabled = false, onClose }: Props) {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [saved, setSaved] = useState<Record<string, Level>>({});   // what the server holds
  const [draft, setDraft] = useState<Record<string, Level>>({});   // what is on screen
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    Promise.all([adminApi.listDashboards(), adminApi.listGrants()])
      .then(([d, g]) => {
        setDashboards(d.data as Dashboard[]);
        const all = g.data as Record<string, Record<string, Level>>;
        const mine = all[email.toLowerCase()] || {};
        setSaved(mine);
        setDraft(mine);
      })
      .catch(() => setError('לא הצלחתי לטעון את ההרשאות'))
      .finally(() => setLoading(false));
  }, [email]);

  // A disabled account may only LOSE access here. Giving is blocked on the server
  // too — this just stops the screen offering a click that would be refused.
  const pick = (dashboardId: string, level: Level) => {
    if (disabled && level !== 'none') return;
    setJustSaved(false);
    setDraft((prev) => {
      const next = { ...prev };
      if (level === 'none') delete next[dashboardId];
      else next[dashboardId] = level;
      return next;
    });
  };

  // Only what actually moved gets written, so a save is a record of a real decision.
  const changed = dashboards
    .map((d) => d.id)
    .filter((id) => levelOf(draft, id) !== levelOf(saved, id));

  const save = async () => {
    if (!changed.length) return;
    setSaving(true);
    setError(null);
    try {
      const payload = Object.fromEntries(changed.map((id) => [id, levelOf(draft, id)]));
      const res = await adminApi.setGrants({ email, grants: payload });
      const now = (res.data as { grants: Record<string, Level> }).grants || {};
      setSaved(now);
      setDraft(now);
      setJustSaved(true);
    } catch {
      setError('השמירה נכשלה — שום הרשאה לא שונתה');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = role === 'admin';
  const envNote = role === 'dev-view' || role === 'dev-viewer'
    ? 'ההרשאות האלה תקפות ב-dev ובפרוד'
    : 'ההרשאות האלה תקפות בפרוד בלבד';

  const closeGuarded = () => {
    if (changed.length && !window.confirm('יש שינויים שלא נשמרו. לסגור בלי לשמור?')) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" dir="rtl">
      <div className="absolute inset-0 bg-black/30" onClick={closeGuarded} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-800">הרשאות דשבורדים</h2>
          <button onClick={closeGuarded} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{email}</p>

        {isAdmin ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            משתמש אדמין — רואה ועורך את כל הדשבורדים בפרוד וב-dev. אין מה לסמן כאן.
          </div>
        ) : loading ? (
          <div className="text-sm text-gray-500 py-8 text-center">טוען…</div>
        ) : (
          <>
            {disabled && (
              <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                החשבון מושבת — אי אפשר לתת לו הרשאות, רק להסיר. כדי לתת גישה, יש להפעיל
                אותו קודם ברשימת המשתמשים.
              </div>
            )}
            <div className="text-xs text-gray-500 mb-3 rounded bg-gray-50 px-3 py-2">{envNote}</div>
            {error && (
              <div className="mb-3 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <GrantPicker
              dashboards={dashboards}
              value={draft}
              baseline={saved}
              disabled={disabled}
              onPick={pick}
            />

            <div className="flex items-center gap-3 pt-4 mt-4 border-t">
              <button
                onClick={save}
                disabled={saving || !changed.length}
                className="text-sm bg-blue-600 disabled:bg-gray-300 text-white rounded-lg px-6 py-2 font-medium"
              >
                {saving ? 'שומר…' : 'שמור'}
              </button>
              <button onClick={closeGuarded} className="text-sm text-gray-500 rounded px-4 py-2">
                {changed.length ? 'ביטול' : 'סגור'}
              </button>
              <span className="text-xs text-gray-500">
                {changed.length
                  ? `${changed.length} שינויים לא נשמרו`
                  : justSaved ? '✓ נשמר' : 'אין שינויים'}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              כל שינוי נרשם עם מי ביצע אותו ומתי. ההרשאות תקפות מיד גם ב-dev וגם בפרוד.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
